import { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { CategoryFilter } from '../components/CategoryFilter';
import { TagFilter } from '../components/TagFilter';
import { useCategories } from '../hooks/useCategories';
import { useTags } from '../hooks/useTags';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Bookmark {
  id: string;
  title: string;
  content: string;
  originalUrl: string;
  backupUrl?: string;
  tags: string[];
  images: string[];
  metadata?: any;
  categoryId?: string;
  createdAt: number;
  updatedAt: number;
}

export default function BookmarksPage() {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [uncategorizedCount, setUncategorizedCount] = useState<number>(0);

  const { categories, loading: categoriesLoading } = useCategories('bookmark');
  const { tags, loading: tagsLoading } = useTags('bookmark');

  useEffect(() => {
    loadBookmarks();
    loadTotalCount();
  }, [selectedCategory, selectedTags]);

  async function loadBookmarks() {
    try {
      setLoading(true);
      const params: any = { category: 'bookmark' };

      if (selectedCategory) {
        params.categoryId = selectedCategory;
      }

      if (selectedTags.length > 0) {
        params.tags = selectedTags.join(',');
      }

      const response = await apiClient.getNotes(params);
      if (response.data?.notes) {
        const formattedBookmarks = response.data.notes.map((note: any) => ({
          id: note.id,
          title: note.title || '无标题',
          content: note.content || '',
          originalUrl: note.original_url || note.originalUrl || note.metadata?.originalUrl || '',
          backupUrl: note.backup_url || note.backupUrl || note.metadata?.backupUrl,
          tags: note.tags || [],
          images: note.images || [],
          metadata: note.metadata,
          categoryId: note.category_id || note.categoryId,
          createdAt: note.created_at || note.createdAt || Date.now(),
          updatedAt: note.updated_at || note.updatedAt || Date.now(),
        }));
        setBookmarks(formattedBookmarks);
      }
    } catch (err) {
      setError('加载书签失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const loadTotalCount = async () => {
    try {
      const result = await apiClient.getNoteStats('bookmark');
      if (result.data) {
        setTotalCount(result.data.count);
        setUncategorizedCount(result.data.uncategorizedCount);
      }
    } catch (error) {
      console.error('Failed to load total count:', error);
    }
  };

  const handleTagSelect = (tagId: string) => {
    const tag = tags.find(t => t.id === tagId);
    if (!tag) return;

    const tagName = tag.name;
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(name => name !== tagName)
        : [...prev, tagName]
    );
  };

  const filteredBookmarks = bookmarks.filter((bookmark) => {
    const matchesSearch =
      searchQuery === '' ||
      bookmark.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bookmark.content.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.every((tagName) => bookmark.tags.includes(tagName));

    return matchesSearch && matchesTags;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="bookmarks-page">
      {/* Header */}
      <div className="bookmarks-header">
        <div className="header-content">
          <h1 className="gradient-text">📚 我的书签</h1>
          <p className="header-subtitle">
            共 <span className="count-badge">{filteredBookmarks.length}</span> 个书签
            {selectedTags.length > 0 || searchQuery ? (
              <>
                {' · 总共 '}
                <span className="count-badge">{bookmarks.length}</span>
                {' 个'}
              </>
            ) : ''}
          </p>
        </div>
        <div className="header-decoration"></div>
      </div>

      <div className="bookmarks-layout">
        {/* Sidebar with Filters */}
        <aside className="bookmarks-sidebar">
          <div className="search-box">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="搜索书签..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => setSearchQuery('')}
                title="清除"
              >
                ✕
              </button>
            )}
          </div>

          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            loading={categoriesLoading}
            totalCount={totalCount}
            uncategorizedCount={uncategorizedCount}
          />

          <TagFilter
            tags={tags}
            selectedTags={selectedTags}
            onSelectTag={handleTagSelect}
            loading={tagsLoading}
          />
        </aside>

        {/* Main Content */}
        <main className="bookmarks-content">
          {/* View Mode Toggle */}
          <div className="view-controls">
            <button
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'active' : ''}
            >
              列表
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'active' : ''}
            >
              网格
            </button>
          </div>

          {/* Bookmarks List/Grid */}
          {filteredBookmarks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <h3>{searchQuery ? '没有找到匹配的书签' : '还没有书签'}</h3>
              <p>{searchQuery ? '尝试其他搜索词' : '使用浏览器插件保存你的第一个书签'}</p>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'bookmarks-grid' : 'bookmarks-list'}>
              {filteredBookmarks.map((bookmark) => (
                <BookmarkCard
                  key={bookmark.id}
                  bookmark={bookmark}
                  viewMode={viewMode}
                  onEdit={setEditingBookmark}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Edit Modal */}
      {editingBookmark && (
        <EditBookmarkModal
          bookmark={editingBookmark}
          categories={categories}
          tags={tags}
          onClose={() => setEditingBookmark(null)}
          onSave={async (updatedData) => {
            try {
              console.log('📤 Sending update data:', updatedData);
              const result = await apiClient.updateNote(editingBookmark.id, updatedData);
              console.log('📥 Update result:', result);
              if (result.error) {
                alert('更新失败：' + result.error.message);
              } else {
                setEditingBookmark(null);
                loadBookmarks();
              }
            } catch (error) {
              alert('更新失败');
              console.error(error);
            }
          }}
        />
      )}

      <style>{`
        .bookmarks-page {
          padding: 12px;
          max-width: 1400px;
          margin: 0 auto;
          min-height: 100vh;
        }

        @media (min-width: 768px) {
          .bookmarks-page {
            padding: 24px;
          }
        }

        .bookmarks-header {
          margin-bottom: var(--spacing-lg);
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
          border-radius: var(--radius-xl);
          padding: var(--spacing-xl);
          box-shadow: var(--shadow-xl);
          position: relative;
          overflow: hidden;
        }

        @media (min-width: 768px) {
          .bookmarks-header {
            margin-bottom: var(--spacing-xl);
            padding: 2rem 2.5rem;
          }
        }

        .header-content {
          position: relative;
          z-index: 2;
        }

        .header-decoration {
          position: absolute;
          top: -50px;
          right: -50px;
          width: 200px;
          height: 200px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          pointer-events: none;
        }

        .bookmarks-header h1 {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 var(--spacing-sm) 0;
          color: white;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        @media (min-width: 768px) {
          .bookmarks-header h1 {
            font-size: 32px;
          }
        }

        .bookmarks-header .gradient-text {
          background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-subtitle {
          color: rgba(255, 255, 255, 0.95);
          margin: 0;
          font-size: 14px;
          font-weight: 500;
        }

        .count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.25);
          padding: 2px 10px;
          border-radius: var(--radius-full);
          font-weight: 700;
          margin: 0 2px;
        }

        .bookmarks-layout {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        @media (min-width: 1024px) {
          .bookmarks-layout {
            display: grid;
            grid-template-columns: 280px 1fr;
            gap: 24px;
          }
        }

        .bookmarks-sidebar {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        @media (min-width: 1024px) {
          .bookmarks-sidebar {
            gap: 16px;
          }
        }

        .search-box {
          background: var(--color-bg-primary);
          border-radius: var(--radius-lg);
          padding: var(--spacing-md);
          box-shadow: var(--shadow-lg);
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          transition: all var(--transition-base);
          border: 2px solid transparent;
        }

        .search-box:focus-within {
          border-color: var(--color-primary);
          box-shadow: var(--shadow-xl), 0 0 0 4px var(--color-primary-light);
        }

        .search-icon {
          font-size: 20px;
          color: var(--color-text-secondary);
          flex-shrink: 0;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 15px;
          background: transparent;
          color: var(--color-text-primary);
        }

        .search-input::placeholder {
          color: var(--color-text-tertiary);
        }

        .search-clear {
          background: var(--color-gray-200);
          border: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: var(--color-text-secondary);
          transition: all var(--transition-fast);
        }

        .search-clear:hover {
          background: var(--color-danger);
          color: white;
          transform: scale(1.1);
        }

        .bookmarks-content {
          background: var(--color-bg-primary);
          border-radius: var(--radius-xl);
          padding: var(--spacing-lg);
          box-shadow: var(--shadow-lg);
          min-height: 400px;
          transition: all var(--transition-base);
        }

        .bookmarks-content:hover {
          box-shadow: var(--shadow-xl);
        }

        @media (min-width: 768px) {
          .bookmarks-content {
            padding: var(--spacing-xl);
          }
        }

        .view-controls {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        @media (min-width: 768px) {
          .view-controls {
            margin-bottom: 20px;
          }
        }

        .view-controls button {
          padding: var(--spacing-sm) var(--spacing-lg);
          border: 2px solid var(--color-gray-200);
          background: var(--color-bg-primary);
          border-radius: var(--radius-md);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all var(--transition-base);
        }

        .view-controls button:hover {
          background: var(--color-gray-50);
          border-color: var(--color-primary);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .view-controls button.active {
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
          color: white;
          border-color: transparent;
          box-shadow: var(--shadow-md);
        }

        .bookmarks-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .bookmarks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .empty-state {
          text-align: center;
          padding: var(--spacing-xl) var(--spacing-lg);
          min-height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .empty-icon {
          font-size: 80px;
          margin-bottom: var(--spacing-lg);
          animation: pulse 2s ease-in-out infinite;
        }

        .empty-state h3 {
          color: var(--color-text-primary);
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 var(--spacing-sm) 0;
        }

        .empty-state p {
          color: var(--color-text-secondary);
          font-size: 15px;
          margin: 0;
        }

        .empty-state h3 {
          font-size: 20px;
          font-weight: 600;
          color: #333;
          margin: 0 0 8px 0;
        }

        .empty-state p {
          color: #666;
          margin: 0;
        }

        @media (max-width: 768px) {
          .bookmarks-page {
            padding: 16px;
          }

          .bookmarks-layout {
            grid-template-columns: 1fr;
          }

          .bookmarks-sidebar {
            order: 2;
          }

          .bookmarks-content {
            order: 1;
          }

          .bookmarks-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div >
  );
}

interface BookmarkCardProps {
  bookmark: Bookmark;
  viewMode: 'list' | 'grid';
  onEdit?: (bookmark: Bookmark) => void;
}

function BookmarkCard({ bookmark, onEdit }: BookmarkCardProps) {
  const [showBackup, setShowBackup] = useState(false);

  const handleOpenOriginal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('Opening URL:', bookmark.originalUrl); // Debug

    if (bookmark.originalUrl) {
      try {
        window.open(bookmark.originalUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Failed to open URL:', error);
        alert(`无法打开链接：${bookmark.originalUrl}`);
      }
    } else {
      alert('该书签没有原始链接');
    }
  };

  const handleOpenBackup = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBackup(true);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4">
      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
        {bookmark.title}
      </h3>

      {/* URL */}
      <a
        href={bookmark.originalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-blue-600 hover:underline mb-2 block truncate"
      >
        {bookmark.originalUrl}
      </a>

      {/* Tags */}
      {bookmark.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {bookmark.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={handleOpenOriginal}
          className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1.5 whitespace-nowrap"
          title="访问原始网站"
        >
          <span>🌐</span>
          <span>原站</span>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const metadata = (bookmark as any).metadata;
            let viewUrl = null;

            if (metadata) {
              try {
                const metadataObj = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
                if (metadataObj.telegramLink) {
                  if (metadataObj.telegramLink.includes('/api/telegram/view-html/')) {
                    viewUrl = metadataObj.telegramLink;
                  }
                }
              } catch (e) {
                console.error('Failed to parse metadata:', e);
              }
            }

            if (viewUrl) {
              window.open(viewUrl, '_blank', 'noopener,noreferrer');
            } else {
              alert('该书签没有完整网页备份');
            }
          }}
          className="px-3 py-1.5 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors flex items-center gap-1.5 whitespace-nowrap"
          title="访问完整备份网页"
        >
          <span>📄</span>
          <span>网页</span>
        </button>
        <button
          onClick={handleOpenBackup}
          className="px-3 py-1.5 bg-gray-500 text-white text-xs rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-1.5 whitespace-nowrap"
          title="查看提取的内容"
        >
          <span>📖</span>
          <span>内容</span>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onEdit) {
              onEdit(bookmark);
            }
          }}
          className="px-3 py-1.5 bg-purple-500 text-white text-xs rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-1.5 whitespace-nowrap"
          title="编辑书签"
        >
          <span>✏️</span>
          <span>编辑</span>
        </button>
        <button
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.confirm('确定要删除这个书签吗？')) {
              try {
                const result = await apiClient.deleteNote(bookmark.id);
                if (result.error) {
                  alert('删除失败：' + result.error.message);
                } else {
                  // Reload bookmarks
                  window.location.reload();
                }
              } catch (error) {
                alert('删除失败');
                console.error(error);
              }
            }
          }}
          className="px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1.5 whitespace-nowrap"
          title="删除书签"
        >
          <span>🗑️</span>
          <span>删除</span>
        </button>
      </div>

      {/* Date */}
      <div className="text-xs text-gray-500 mt-3">
        {new Date(bookmark.createdAt).toLocaleDateString('zh-CN')}
      </div>

      {/* Backup Modal */}
      {showBackup && (
        <div className="backup-modal-overlay" onClick={() => setShowBackup(false)}>
          <div className="backup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="backup-modal-header">
              <h3>{bookmark.title}</h3>
              <button onClick={() => setShowBackup(false)}>×</button>
            </div>
            <div className="backup-modal-content">
              {/* 备份内容 - 智能渲染 HTML 或纯文本 */}
              <div className="backup-content-wrapper">
                {bookmark.content ? (
                  // 检测是否为 HTML 内容
                  bookmark.content.trim().startsWith('<') ? (
                    <div
                      className="backup-html-content"
                      dangerouslySetInnerHTML={{ __html: bookmark.content }}
                      style={{
                        lineHeight: '1.6',
                        fontSize: '15px',
                        color: '#333'
                      }}
                    />
                  ) : (
                    <div
                      className="backup-text-content"
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: '1.6',
                        fontSize: '15px',
                        color: '#333'
                      }}
                    >
                      {bookmark.content}
                    </div>
                  )
                ) : (
                  <div className="no-backup">暂无备份内容</div>
                )}
              </div>

              {/* 图片展示 */}
              {bookmark.images && bookmark.images.length > 0 && (
                <div className="backup-images">
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#333'
                  }}>
                    📷 图片 ({bookmark.images.length})
                  </h4>
                  <div className="image-grid">
                    {bookmark.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Image ${idx + 1}`}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          marginTop: '12px',
                          cursor: 'pointer',
                          transition: 'transform 0.2s'
                        }}
                        onClick={() => {
                          // 点击图片在新标签页中打开
                          window.open(img, '_blank');
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .backup-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .backup-modal {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }

        .backup-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
        }

        .backup-modal-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #333;
          flex: 1;
          padding-right: 20px;
        }

        .backup-modal-header button {
          background: none;
          border: none;
          font-size: 32px;
          color: #999;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .backup-modal-header button:hover {
          background: #f0f0f0;
          color: #666;
        }

        .backup-modal-content {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
          background: #fafafa;
        }

        .backup-content-wrapper {
          background: white;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .backup-text-content,
        .backup-html-content {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        .backup-html-content {
          /* 重置和美化 HTML 内容样式 */
        }

        .backup-html-content p,
        .backup-text-content p {
          margin: 0 0 12px 0;
        }

        .backup-html-content h1,
        .backup-html-content h2,
        .backup-html-content h3,
        .backup-text-content h1,
        .backup-text-content h2,
        .backup-text-content h3 {
          margin: 20px 0 12px 0;
          font-weight: 600;
          color: #333;
        }

        .backup-html-content h1 {
          font-size: 24px;
        }

        .backup-html-content h2 {
          font-size: 20px;
        }

        .backup-html-content h3 {
          font-size: 18px;
        }

        .backup-html-content ul,
        .backup-html-content ol,
        .backup-text-content ul,
        .backup-text-content ol {
          margin: 12px 0;
          padding-left: 24px;
        }

        .backup-html-content li,
        .backup-text-content li {
          margin: 6px 0;
        }

        .backup-html-content a,
        .backup-text-content a {
          color: #007bff;
          text-decoration: none;
        }

        .backup-html-content a:hover,
        .backup-text-content a:hover {
          text-decoration: underline;
        }

        .backup-html-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 12px 0;
        }

        .backup-html-content blockquote {
          border-left: 4px solid #e0e0e0;
          padding-left: 16px;
          margin: 16px 0;
          color: #666;
          font-style: italic;
        }

        .backup-html-content code {
          background: #f5f5f5;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 14px;
        }

        .backup-html-content pre {
          background: #f5f5f5;
          padding: 12px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 12px 0;
        }

        .backup-html-content pre code {
          background: none;
          padding: 0;
        }

        .backup-html-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }

        .backup-html-content th,
        .backup-html-content td {
          border: 1px solid #e0e0e0;
          padding: 8px 12px;
          text-align: left;
        }

        .backup-html-content th {
          background: #f5f5f5;
          font-weight: 600;
        }

        .backup-text-content code {
          background: #f5f5f5;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 0.9em;
        }

        .backup-text-content pre {
          background: #f5f5f5;
          padding: 12px;
          border-radius: 6px;
          overflow-x: auto;
          margin: 12px 0;
        }

        .backup-text-content blockquote {
          border-left: 4px solid #e0e0e0;
          padding-left: 16px;
          margin: 12px 0;
          color: #666;
        }

        .no-backup {
          text-align: center;
          color: #999;
          padding: 40px 20px;
          font-size: 14px;
        }

        .backup-images {
          background: white;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .image-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .image-grid img {
          width: 100%;
          height: auto;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .backup-text {
          line-height: 1.6;
          color: #333;
        }

        .backup-text img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 12px 0;
        }

        @media (max-width: 768px) {
          .backup-modal {
            max-width: 100%;
            max-height: 100vh;
            border-radius: 0;
          }

          .image-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

interface EditBookmarkModalProps {
  bookmark: Bookmark;
  categories: any[];
  tags: any[];
  onClose: () => void;
  onSave: (data: any) => void;
}

function EditBookmarkModal({ bookmark, categories, tags, onClose, onSave }: EditBookmarkModalProps) {
  // 从 bookmark 中获取 categoryId (可能在 metadata 中或直接在对象上)
  const initialCategoryId = (() => {
    // 首先检查是否有 categoryId 字段
    if ((bookmark as any).categoryId) {
      return (bookmark as any).categoryId;
    }
    // 然后检查 metadata 中是否有 categoryId
    try {
      const metadata = typeof bookmark.metadata === 'string'
        ? JSON.parse(bookmark.metadata)
        : bookmark.metadata;
      if (metadata?.categoryId) {
        return metadata.categoryId;
      }
    } catch (e) {
      console.error('Failed to parse metadata:', e);
    }
    return '';
  })();

  const [title, setTitle] = useState(bookmark.title);
  const [content, setContent] = useState(bookmark.content || '');
  const [categoryId, setCategoryId] = useState<string>(initialCategoryId);
  const [selectedTags, setSelectedTags] = useState<string[]>(bookmark.tags || []);
  const [newTag, setNewTag] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 构建更新数据
    const updateData: any = {
      title,
      content,
      tags: selectedTags,
      categoryId: categoryId && categoryId !== '' ? categoryId : null, // 空字符串转为 null
    };

    console.log('📤 Submitting update with categoryId:', updateData.categoryId);
    onSave(updateData);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !selectedTags.includes(newTag.trim())) {
      setSelectedTags([...selectedTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter(t => t !== tag));
  };

  const handleToggleTag = (tagName: string) => {
    if (selectedTags.includes(tagName)) {
      setSelectedTags(selectedTags.filter(t => t !== tagName));
    } else {
      setSelectedTags([...selectedTags, tagName]);
    }
  };

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h2>✏️ 编辑书签</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-modal-form">
          {/* 标题 */}
          <div className="form-group">
            <label>标题</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="书签标题"
              required
            />
          </div>

          {/* URL (只读) */}
          <div className="form-group">
            <label>网址</label>
            <input
              type="text"
              value={bookmark.originalUrl}
              disabled
              className="disabled-input"
            />
          </div>

          {/* 内容编辑器 */}
          <div className="form-group">
            <label>内容</label>
            <div className="editor-wrapper">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    [{ 'color': [] }, { 'background': [] }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
                placeholder="编辑书签内容..."
              />
            </div>
          </div>

          {/* 分类选择 */}
          <div className="form-group">
            <label>分类</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">未分类</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon || '📁'} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* 标签选择 */}
          <div className="form-group">
            <label>标签</label>

            {/* 已选标签 */}
            <div className="selected-tags">
              {selectedTags.map((tag) => (
                <span key={tag} className="tag-chip">
                  #{tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="tag-remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {/* 添加新标签 */}
            <div className="tag-input-group">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="输入新标签..."
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="add-tag-btn"
              >
                添加
              </button>
            </div>

            {/* 现有标签列表 */}
            {tags.length > 0 && (
              <div className="existing-tags">
                <p className="existing-tags-label">选择现有标签：</p>
                <div className="tag-list">
                  {tags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleToggleTag(tag.name)}
                      className={`tag-option ${selectedTags.includes(tag.name) ? 'selected' : ''}`}
                    >
                      #{tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 操作按钮 */}
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              取消
            </button>
            <button type="submit" className="save-btn">
              保存
            </button>
          </div>
        </form>

        <style>{`
          .edit-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .edit-modal {
            background: white;
            border-radius: 12px;
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }

          .edit-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #e0e0e0;
            position: sticky;
            top: 0;
            background: white;
            z-index: 1;
          }

          .edit-modal-header h2 {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
            color: #333;
          }

          .close-btn {
            background: none;
            border: none;
            font-size: 32px;
            color: #999;
            cursor: pointer;
            padding: 0;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.2s;
          }

          .close-btn:hover {
            background: #f0f0f0;
            color: #666;
          }

          .edit-modal-form {
            padding: 20px;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #333;
            font-size: 14px;
          }

          .form-group input,
          .form-group select {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            font-size: 14px;
            outline: none;
            transition: border-color 0.2s;
          }

          .form-group input:focus,
          .form-group select:focus {
            border-color: #007bff;
          }

          .form-group input.disabled-input {
            background: #f5f5f5;
            color: #999;
            cursor: not-allowed;
          }

          .editor-wrapper {
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            overflow: hidden;
          }

          .editor-wrapper :global(.ql-container) {
            min-height: 200px;
            font-size: 14px;
          }

          .editor-wrapper :global(.ql-editor) {
            min-height: 200px;
          }

          .editor-wrapper :global(.ql-toolbar) {
            border: none;
            border-bottom: 1px solid #e0e0e0;
            background: #f9f9f9;
          }

          .selected-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 12px;
            min-height: 32px;
          }

          .tag-chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            background: #e3f2fd;
            color: #007bff;
            border-radius: 16px;
            font-size: 13px;
            font-weight: 500;
          }

          .tag-remove {
            background: none;
            border: none;
            color: #007bff;
            font-size: 18px;
            cursor: pointer;
            padding: 0;
            width: 16px;
            height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.2s;
          }

          .tag-remove:hover {
            background: rgba(0, 123, 255, 0.1);
          }

          .tag-input-group {
            display: flex;
            gap: 8px;
            margin-bottom: 12px;
          }

          .tag-input-group input {
            flex: 1;
          }

          .add-tag-btn {
            padding: 10px 16px;
            background: #007bff;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
            white-space: nowrap;
          }

          .add-tag-btn:hover {
            background: #0056b3;
          }

          .existing-tags {
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid #e0e0e0;
          }

          .existing-tags-label {
            font-size: 13px;
            color: #666;
            margin: 0 0 8px 0;
          }

          .tag-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .tag-option {
            padding: 6px 12px;
            background: #f5f5f5;
            color: #666;
            border: 1px solid #e0e0e0;
            border-radius: 16px;
            font-size: 13px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .tag-option:hover {
            background: #e0e0e0;
            border-color: #ccc;
          }

          .tag-option.selected {
            background: #e3f2fd;
            color: #007bff;
            border-color: #007bff;
          }

          .modal-actions {
            display: flex;
            gap: 12px;
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
          }

          .cancel-btn,
          .save-btn {
            flex: 1;
            padding: 12px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .cancel-btn {
            background: #f0f0f0;
            color: #333;
          }

          .cancel-btn:hover {
            background: #e0e0e0;
          }

          .save-btn {
            background: #007bff;
            color: white;
          }

          .save-btn:hover {
            background: #0056b3;
          }

          @media (max-width: 768px) {
            .edit-modal {
              max-width: 100%;
              max-height: 100vh;
              border-radius: 0;
            }
          }
        `}</style>
      </div>
    </div>
  );
}
